import React, { useEffect, useRef, useState } from 'react';
import styles from './Header.module.scss';
import {
	Button,
	InputField,
	Spacer,
	Text,
	H1,
	H2,
	H3,
	H4,
	H5
} from '@components';
import { BUTTON_TYPES, INPUT_ICON_POS, SPACER_DIRECTION, TEXT_WEIGHT } from '@types';
import SearchIcon from '@mui/icons-material/Search';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CloseIcon from '@mui/icons-material/Close';
import { PanelState } from '../Table';

type HeaderProps = {
    tableTitle?: string | JSX.Element | null;
	titleTextStyle?: 'H1' | 'H2' | 'H3' | 'H4' | 'H5'
    canSearch?: boolean;
    panelOpen?: boolean;
    setPanelOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterPanel?: React.Dispatch<React.SetStateAction<boolean>>;
    setSettingsPanel?: React.Dispatch<React.SetStateAction<boolean>>;
    settingsPanel?: boolean;
    filterPanel?: boolean;
    canChangeDisplaySettings?: boolean;
    canFilter?: boolean;
    searchPlaceholder?: string | null;
    tableFilters?: any[];
    applyFilter: (id: string | null) => void;
    loadedFilter?: any;
    onSearch?: (value: string) => void;
    searchFromParent?: string;
    openPanel?: (panelName: 'filter' | 'settings' | null) => void;
    closePanel?: () => void;
    panelState?: PanelState;
    buttonComponents?:any;
	fullWidthSearch?: boolean;
	downloadCsvPlugin?: React.ReactNode;
}

const Header = ({
	tableTitle,
	titleTextStyle = 'H2',
	canSearch = true,
	canChangeDisplaySettings = true,
	canFilter = true,
	searchPlaceholder = null,
	tableFilters = [],
	applyFilter,
	loadedFilter,
	onSearch,
	searchFromParent = '',
	openPanel,
	closePanel,
	panelState,
	buttonComponents,
	fullWidthSearch,
	downloadCsvPlugin,
}: HeaderProps) => {
	const [searchVal, setSearchVal] = useState(searchFromParent);
	const [toggleFilterList, setToggleFilterList] = useState(false);
	const filterListContainer = useRef<any>(null);
	const savedFiltersButton = useRef<any>(null);
	const [filterSearchValue, setFilterSearchValue] = useState('');
	const search = (e: any) => {
		const val = e.target.value;
		if (onSearch) {
			onSearch(val);
			setSearchVal(val);
		}
	}

	const onPaste = (e:any) => {
		e.preventDefault();
		e.stopPropagation();
		let cb = e.clipboardData.getData('text/plain');
		cb = cb.replace(/,/g, ' ');
		cb = cb.trim();
		cb = cb.replace(/\s+/g, ',');
		if (onSearch) {
			onSearch(cb);
			setSearchVal(cb);
		}
	}

	const ApplyFilter = (filterId: string) => {
		setToggleFilterList(false);
		applyFilter(filterId);
	}

	const toggleSidePanel = (type: 'filter' | 'settings') => {
		if (panelState?.isOpen && panelState.activePanel === type) {
			closePanel?.();
			return;
		}
		openPanel?.(type);
	}
	const clearLoadedFilter = (e: any) => {
		e.stopPropagation();
		e.preventDefault();
		applyFilter(null);
	}
	const openFilterPanel = () => {
		if (loadedFilter) {
			openPanel?.('filter');
			setToggleFilterList(false);
		}
	}
	const clearAll = () => {
		applyFilter(null);
		setToggleFilterList(false);
	}
	const generateTableTitle = () => {
		if (tableTitle === '' || !tableTitle) return null;
		if (typeof tableTitle === 'string') {
			switch (titleTextStyle) {
			case 'H1':
				return <H1 weight={TEXT_WEIGHT.BOLD} text={tableTitle} />;
			case 'H3':
				return <H3 weight={TEXT_WEIGHT.BOLD} text={tableTitle} />;
			case 'H4':
				return <H4 weight={TEXT_WEIGHT.BOLD} text={tableTitle} />;
			case 'H5':
				return <H5 weight={TEXT_WEIGHT.BOLD} text={tableTitle} />;
			case 'H2':
			default:
				return <H2 weight={TEXT_WEIGHT.BOLD} text={tableTitle} />;
			}
		}
		return tableTitle;
	}
	useEffect(() => {
		const handleClickOutside = (event: any) => {
			if (
				(filterListContainer.current && !filterListContainer?.current?.contains(event.target)) &&
                (savedFiltersButton.current && !savedFiltersButton?.current?.contains(event.target))
			) {
				setToggleFilterList(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
	useEffect(() => {
		if (!searchVal) {
			setSearchVal(searchFromParent);
			if (searchFromParent && onSearch) {
				onSearch(searchFromParent)
			}
		}
	}, [searchFromParent]);

	return (
		<header className={styles.header}>
			{!fullWidthSearch && <>
				<div className={styles.tableTitle}>
					{generateTableTitle()}
				</div>
				<div className={styles.actionMenuContainer}>
					<div className={styles.buttonsContainer}>
						{
							buttonComponents && (
								<>
									{buttonComponents}
									<Spacer dir={SPACER_DIRECTION.VERTICAL} size={10} />
								</>
							)
						}
						{downloadCsvPlugin &&
							<>
								{downloadCsvPlugin}
								<Spacer dir={SPACER_DIRECTION.VERTICAL} size={10} />
							</>
						}
						{canFilter &&
							<button
								className={`
									${styles.button}
									${styles.ctaButton}
									${panelState?.isOpen && panelState.activePanel === 'filter' ? styles.activeBtn : ''}
								`}
								onClick={() => { toggleSidePanel('filter') }}
								aria-label={'Toggle Filter Side Panel'}
							>
								<FilterIcon />
								<Text text={'Filter'} />
							</button>
						}
						{canFilter && (tableFilters.length > 0 || loadedFilter) &&
							<div className={styles.buttonDropDownContainer}>
								<Spacer dir={SPACER_DIRECTION.VERTICAL} size={10} />
								<button
									ref={savedFiltersButton}
									className={`${styles.button} ${toggleFilterList || (loadedFilter) ? styles.activeBtn : ''}`}
									onClick={() => { setToggleFilterList(!toggleFilterList) }}
									aria-label={'Toggle Filter List'}
								>
									{
										loadedFilter ?
											<BookmarkIcon style={{ color: 'var(--brand-orange-dark)' }} /> :
											<BookmarkBorderIcon style={{ color: toggleFilterList ? 'var(--brand-orange-dark)' : 'var(--brand-navy-default)' }} />
									}
									<Text
										text={
											loadedFilter
												? loadedFilter.filterName || 'Temp Filter'
												: 'Saved Filters'
										}
										color={toggleFilterList || (loadedFilter) ? 'var(--brand-orange-dark)' : 'var(--brand-navy-default)'}
									/>
									{loadedFilter && <CloseIcon onClick={(e) => { clearLoadedFilter(e) }} style={{ color: 'var(--brand-orange-dark)' }} />}
								</button>
								{toggleFilterList && <div ref={filterListContainer} className={styles.filterListContainer}>
									<div className={styles.inputWrapper}>
										<InputField
											name={'Filter Search Value'}
											value={filterSearchValue}
											onChange={(e: any) => setFilterSearchValue(e.target.value)}
											placeholder={'Start typing filter name'}
											Icon={SearchIcon}
											iconPos={INPUT_ICON_POS.RIGHT}
											className={styles.filterSearch}
											displayThin
											keepErrorSpace={false}
											autoComplete='off'
										/>
									</div>
									<div className={styles.filterScrollContainer}>
										{tableFilters.filter((filter) => filter.filterName.toLowerCase().includes(filterSearchValue.toLowerCase())).length > 0 && <>
											<div className={styles.filterHeading}>
												<Text type={'label'} weight={TEXT_WEIGHT.BOLD} text={'My Filters'} />
											</div>
											{tableFilters.filter((filter) => filter.filterName.toLowerCase().includes(filterSearchValue.toLowerCase())).map((filter, i) => {
												return <button className={styles.filterButton} key={i} onClick={() => ApplyFilter(filter.id)} aria-label={`Apply Filter ${filter.filterName}`}>{filter.filterName}</button>
											})}
										</>}
										{loadedFilter && <div className={styles.filterListButtons}>
											<Button type={BUTTON_TYPES.TERTIARY} text={'Clear All'} onClick={() => { clearAll() }} />
											{!loadedFilter.type && <Button type={BUTTON_TYPES.TERTIARY} text={'Edit Filters'} onClick={() => { openFilterPanel() }} />}
										</div>}
									</div>
								</div>}
							</div>}
						{canChangeDisplaySettings && <>
							<Spacer dir={SPACER_DIRECTION.VERTICAL} size={10} />
							<button
								className={`${styles.button} ${styles.ctaButton} ${panelState?.isOpen && panelState.activePanel === 'settings' ? styles.activeBtn : ''}`}
								onClick={() => { toggleSidePanel('settings') }}
								aria-label={'Toggle Settings Side Panel'}
							>
								<TuneIcon />
								<Text text={'Display settings'} />
							</button>
						</>}
					</div>
					<Spacer size={10} dir={SPACER_DIRECTION.VERTICAL} />
					{canSearch && <div className={styles.searchContainer}>
						<InputField name={'Search'} autoComplete='off' onPaste={(e) => { console.log(e); onPaste(e) }} displayThin className={styles.searchInput} data-testid={'search-shipment'} placeholder={searchPlaceholder ? searchPlaceholder : `Search ${typeof tableTitle === 'string' ? tableTitle : ''}`} value={searchVal} onChange={(e: any) => search(e)} Icon={SearchIcon} keepErrorSpace={false} />
					</div>}
				</div>
			</>}
			{fullWidthSearch && canSearch && <InputField name={'Search'} autoComplete='off' onPaste={onPaste} displayThin stretch data-testid={'search-shipment'} placeholder={searchPlaceholder ? searchPlaceholder : `Search ${typeof tableTitle === 'string' ? tableTitle : ''}`} value={searchVal} onChange={(e: any) => search(e)} Icon={SearchIcon} keepErrorSpace={false} />}
		</header>
	)
}

export default Header;
